/**
 * One governed layout composer for forms, pages and dashboards (#1860).
 *
 * This edits the same finite `LayoutDocument` a solution transports. It owns
 * no persistence, field metadata, authority or executable expression: the host
 * supplies the governed catalogue and saves through its existing door. Pointer
 * drag and the overflow menu call the same immutable operations, so mouse,
 * keyboard, touch and AI authors cannot create different document shapes.
 */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions -- HTML drag
 * targets are the visible section/item regions. Each operation also has an
 * adjacent keyboard/touch overflow action; making the region a button would
 * illegally nest the actual form controls it contains. */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { GripVertical, Monitor, Plus, RotateCcw, Search, Smartphone, Tablet } from 'lucide-react'
import {
  addLayoutSection,
  canonicalLayoutDocument,
  mergeLayoutSections,
  moveLayoutSection,
  placeLayoutItem,
  publishLayoutRevision,
  removeLayoutSection,
  renameLayoutSection,
  normalizeLayoutDocument,
  setLayoutItemHidden,
  setLayoutItemProfileSpan,
  setLayoutSectionProfileColumns,
  splitLayoutSection,
  unplaceLayoutItem,
  type LayoutCatalog as GovernedLayoutCatalog,
  type LayoutDocumentV2,
  type PersistedLayoutDocument as GovernedPersistedLayoutDocument,
  type LayoutDocument as GovernedLayoutDocument,
} from '@solum/experience'
import { Button } from '../controls/Button'
import { Input } from '../controls/Input'
import { Select } from '../controls/Select'
import { EntityIcon } from '../EntityIcon'
import { CustomizeRowMenu } from '../shell/CustomizeRowMenu'
import { ActionBar } from '../work/ActionBar'
import s from './LayoutComposer.module.css'

export type LayoutPreview = 'desktop' | 'tablet' | 'phone'

export interface LayoutComposerProps {
  /**
   * Either grammar. A v1 document is normalized once on the way in and edited
   * as v2 from then on, which is what makes "the next authored save publishes
   * v2" true without a read-time rewrite anywhere else (#2009).
   */
  value: PersistedLayoutDocument
  catalog: LayoutCatalog
  /** Labels are presentation facts resolved from the metamodel/widget registry. */
  itemLabels?: Readonly<Record<string, string>>
  /** The actual cascade layer being edited, e.g. “Sales persona default”. */
  layerLabel: string
  /** Reconciliation/compatibility findings supplied by the governed host. */
  issues?: readonly string[]
  busy?: boolean
  /**
   * A personal preference is deliberately only a permutation of the governed
   * definition. Definition authors may add, split, hide and move fields across
   * sections; a user may only reorder the sections and the fields already in
   * each one. Keeping that distinction here prevents hosts from growing a
   * second, more powerful personal-layout document by accident.
   */
  editMode?: 'definition' | 'order'
  /** Host-owned layer selector. The composer owns editing, never authority. */
  layerControl?: ReactNode
  /**
   * WHERE the document being edited came from (#2009).
   *
   * Host-supplied, because the rung ladder is a server fact and this component
   * must not learn to read it. An author choosing a target needs to know
   * whether they are editing that target's own layer or one it inherits — those
   * look identical on screen, and saving the second freezes a copy of somebody
   * else's layer into this address.
   */
  provenance?: ReactNode
  /** Always v2: an author who touched this editor has authored responsive
   *  geometry, whether or not they visited every profile. */
  onSave: (document: LayoutDocumentV2) => void
  onCancel: () => void
  onReset?: () => void
}

const nameOf = (item: { key: string }, labels?: Readonly<Record<string, string>>) => labels?.[item.key] ?? item.key

const newSectionId = (document: PersistedLayoutDocument): string => {
  const used = new Set(document.sections.map((section) => section.id))
  let number = document.sections.length + 1
  while (used.has(`section_${number}`)) number += 1
  return `section_${number}`
}

export function LayoutComposer({
  value, catalog, itemLabels, layerLabel, issues = [], busy = false, editMode = 'definition',
  layerControl, provenance, onSave, onCancel, onReset,
}: LayoutComposerProps) {
  const [draft, setDraft] = useState<LayoutDocumentV2>(() => normalizeLayoutDocument(value))
  const [preview, setPreview] = useState<LayoutPreview>('desktop')
  const [filter, setFilter] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [renaming, setRenaming] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [dragged, setDragged] = useState<{ itemId: string; sectionId: string } | null>(null)
  const [draggedSection, setDraggedSection] = useState<string | null>(null)
  const [message, setMessage] = useState('')

  /* Both sides normalized. Opening the editor on a v1 document and touching
     nothing is not an edit: the save would publish v2, but telling somebody
     their untouched form is a draft would be a lie about what they did. */
  const baseline = useMemo(() => normalizeLayoutDocument(value), [value])
  useEffect(() => { setDraft(baseline) }, [baseline])
  const dirty = canonicalLayoutDocument(draft) !== canonicalLayoutDocument(baseline)
  const available = useMemo(() => {
    const needle = filter.trim().toLocaleLowerCase()
    return draft.unplaced.filter((item) => !needle || nameOf(item, itemLabels).toLocaleLowerCase().includes(needle))
  }, [draft.unplaced, filter, itemLabels])

  const change = (next: LayoutDocumentV2, announcement: string) => {
    setDraft(next)
    setMessage(announcement)
  }
  const addSection = () => {
    const label = newLabel.trim()
    if (!label) return
    change(addLayoutSection(draft, { id: newSectionId(draft), label, columns: 2 }), `Added ${label}`)
    setNewLabel('')
  }
  const startRename = (id: string, label: string) => { setRenaming(id); setRenameValue(label) }
  const finishRename = () => {
    if (!renaming || !renameValue.trim()) return
    change(renameLayoutSection(draft, renaming, renameValue), `Renamed section to ${renameValue.trim()}`)
    setRenaming(null)
  }
  const splitSection = (sectionId: string, sectionLabel: string, at: number) => {
    const label = `${sectionLabel} continued`
    change(splitLayoutSection(draft, sectionId, at, {
      id: newSectionId(draft),
      label,
      columns: draft.sections.find((section) => section.id === sectionId)?.columns ?? 1,
    }), `Split ${sectionLabel}; ${label} begins with the selected field`)
  }
  const save = () => {
    try { onSave(publishLayoutRevision(draft, catalog)) }
    catch (error) { setMessage(error instanceof Error ? error.message : 'This layout cannot be saved') }
  }

  return (
    <div className={s.composerBox} data-testid="layout-composer-box">
    <section className={s.composer} data-testid="layout-composer" data-preview={preview}
      aria-label={`Customize ${layerLabel}`}>
      <header className={s.toolbar}>
        <div>
          <strong>Customize experience</strong>
          <p>Editing <b>{layerLabel}</b>. Changes remain a draft until you save.</p>
          {provenance}
        </div>
        {layerControl}
        <div className={s.preview} role="group" aria-label="Preview size">
          {([
            ['desktop', Monitor, 'Desktop'], ['tablet', Tablet, 'Tablet'], ['phone', Smartphone, 'Phone'],
          ] as const).map(([key, Icon, label]) => (
            <Button key={key} size="sm" variant={preview === key ? 'secondary' : 'ghost'}
              icon={<Icon />} aria-pressed={preview === key} onClick={() => setPreview(key)}>{label}</Button>
          ))}
        </div>
      </header>

      {editMode === 'definition' && <div className={s.addSection}>
        <Input value={newLabel} placeholder="New section name" aria-label="New section name"
          onChange={(event) => setNewLabel(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addSection() } }} />
        <Button size="sm" icon={<Plus />} disabled={!newLabel.trim() || busy} onClick={addSection}>Add section</Button>
      </div>}

      {issues.length > 0 && (
        <div className={s.issues} role="alert" data-testid="layout-issues">
          <strong>This layout needs attention before it can travel.</strong>
          <ul>{issues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
        </div>
      )}

      <div className={s.canvas} data-testid="layout-composer-canvas">
        {draft.sections.map((section, sectionPosition) => {
          /* The geometry this section has ON THE PROFILE BEING PREVIEWED.
             Both controls below read it, which is what makes switching the
             preview move the column selector and the span selectors together
             — they are two views of one number, not two settings. */
          const columns = section.columns[preview]
          /* The grammar's own ceiling, not a composer preference: a phone track
             admits at most two columns (`layout.ts` profileMaximum). */
          const maxColumns = preview === 'phone' ? 2 : 4
          return (
          <section key={section.id} className={s.section} data-testid={`layout-section-${section.id}`}
            role="group" aria-label={`${section.label} layout section`}
            data-dragging={draggedSection === section.id || undefined}
            onDragOver={(event) => { if (draggedSection) event.preventDefault() }}
            onDrop={(event) => {
              if (!draggedSection) return
              event.preventDefault()
              change(moveLayoutSection(draft, draggedSection, sectionPosition),
                `Moved section to position ${sectionPosition + 1}`)
              setDraggedSection(null)
            }}>
            <header className={s.sectionHead}>
              <button type="button" className={s.grip} draggable={!busy}
                aria-label={`Drag ${section.label} section`}
                onDragStart={() => setDraggedSection(section.id)}
                onDragEnd={() => setDraggedSection(null)}><GripVertical /></button>
              {renaming === section.id ? (
                <Input autoFocus value={renameValue} aria-label={`Rename ${section.label}`}
                  onChange={(event) => setRenameValue(event.target.value)}
                  onBlur={finishRename}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') finishRename()
                    if (event.key === 'Escape') setRenaming(null)
                  }} />
              ) : <strong>{section.label}</strong>}
              <span className={s.grow} />
              {editMode === 'definition' && <Select value={String(columns)}
                aria-label={`Columns in ${section.label} on ${preview}`}
                options={Array.from({ length: maxColumns }, (_, index) => ({
                  value: String(index + 1), label: `${index + 1} column${index === 0 ? '' : 's'}`,
                }))}
                onValueChange={(next) => change(
                  /* Writes ONE track. The other two profiles keep the counts
                     they were authored with — the operation is the guarantee,
                     not this call site. */
                  setLayoutSectionProfileColumns(draft, section.id, preview, Number(next)),
                  `${section.label} now uses ${next} ${preview} column${next === '1' ? '' : 's'}`,
                 )} />}
              <CustomizeRowMenu label={section.label}
                canMoveUp={sectionPosition > 0} canMoveDown={sectionPosition < draft.sections.length - 1}
                onMoveUp={() => change(moveLayoutSection(draft, section.id, sectionPosition - 1), `Moved ${section.label} up`)}
                onMoveDown={() => change(moveLayoutSection(draft, section.id, sectionPosition + 1), `Moved ${section.label} down`)}
                onRename={editMode === 'definition' ? () => startRename(section.id, section.label) : undefined}
                onDelete={editMode === 'definition' ? () => change(removeLayoutSection(draft, section.id), `Removed ${section.label}; its items are unplaced`) : undefined}
                actions={editMode === 'definition' && sectionPosition > 0 ? [{
                  key: 'merge-previous', label: 'Merge with previous section',
                  onSelect: () => change(
                    mergeLayoutSections(draft, section.id, draft.sections[sectionPosition - 1]!.id),
                    `Merged ${section.label} into ${draft.sections[sectionPosition - 1]!.label}`,
                  ),
                }] : []} />
            </header>
            <div className={s.items} data-columns={columns}>
              {section.items.map((item, itemPosition) => {
                const required = Boolean(catalog.items.find((candidate) => candidate.id === item.id)?.required)
                const span = item.span[preview]
                /* Was `preview === 'phone' ? 1 : section.columns` — a phone was
                   pinned to one column no matter what the document said, and
                   tablet silently borrowed the desktop count. Both are now the
                   authored track for this profile. */
                const maxSpan = columns
                return <article key={item.id} className={s.item} data-hidden={item.hidden || undefined}
                  role="group" aria-label={`${nameOf(item, itemLabels)} layout item`}
                  style={{ gridColumn: `span ${Math.min(span, maxSpan)}` }}
                  onDragOver={(event) => { if (dragged) event.preventDefault() }}
                  onDrop={(event) => {
                    if (!dragged || (editMode === 'order' && dragged.sectionId !== section.id)) return
                    event.preventDefault()
                    event.stopPropagation()
                    change(placeLayoutItem(draft, dragged.itemId, section.id, itemPosition),
                      `Moved ${dragged.itemId} to position ${itemPosition + 1} in ${section.label}`)
                    setDragged(null)
                  }}>
                  <button type="button" className={s.grip} draggable={!busy}
                    aria-label={`Drag ${nameOf(item, itemLabels)}`}
                    onDragStart={() => setDragged({ itemId: item.id, sectionId: section.id })} onDragEnd={() => setDragged(null)}>
                    <GripVertical />
                  </button>
                  <EntityIcon iconKey={item.kind} size={16} />
                  <span>{nameOf(item, itemLabels)}</span>
                  <span className={s.kind}>{item.kind}</span>
                  <span className={s.grow} />
                  {editMode === 'definition' && <Select value={String(Math.min(span, maxSpan))}
                    aria-label={`Width on ${preview} for ${nameOf(item, itemLabels)}`}
                    options={Array.from({ length: maxSpan }, (_, index) => ({
                      value: String(index + 1), label: `${index + 1}/${maxSpan}`,
                    }))}
                    onValueChange={(next) => change(
                      setLayoutItemProfileSpan(draft, item.id, preview, Number(next)),
                      `${nameOf(item, itemLabels)} uses ${next} ${preview} column${next === '1' ? '' : 's'}`,
                    )} />}
                  <CustomizeRowMenu label={nameOf(item, itemLabels)} hidden={item.hidden}
                    canMoveUp={itemPosition > 0} canMoveDown={itemPosition < section.items.length - 1}
                    sections={editMode === 'definition' ? draft.sections.map((target) => ({
                      key: target.id, label: target.label, current: target.id === section.id,
                      onSelect: () => change(placeLayoutItem(draft, item.id, target.id), `Moved ${nameOf(item, itemLabels)} to ${target.label}`),
                    })) : undefined}
                    onMoveUp={() => change(placeLayoutItem(draft, item.id, section.id, itemPosition - 1), `Moved ${nameOf(item, itemLabels)} up`)}
                    onMoveDown={() => change(placeLayoutItem(draft, item.id, section.id, itemPosition + 1), `Moved ${nameOf(item, itemLabels)} down`)}
                    onToggleHidden={editMode === 'definition' && !required ? () => change(setLayoutItemHidden(draft, item.id, !item.hidden), `${item.hidden ? 'Showed' : 'Hid'} ${nameOf(item, itemLabels)}`) : undefined}
                    actions={editMode === 'definition' ? [
                      ...(itemPosition > 0 ? [{
                        key: 'split-here', label: 'Split into a new section here',
                        onSelect: () => splitSection(section.id, section.label, itemPosition),
                      }] : []),
                      ...(!required ? [{
                        key: 'unplace', label: 'Remove from layout', danger: true,
                        onSelect: () => change(unplaceLayoutItem(draft, item.id, catalog), `Removed ${nameOf(item, itemLabels)} from the layout`),
                      }] : []),
                    ] : []} />
                </article>
              })}
              {dragged && (editMode === 'definition' || dragged.sectionId === section.id) && (
                <button type="button" className={s.dropTarget}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault()
                    change(placeLayoutItem(draft, dragged.itemId, section.id), `Moved ${dragged.itemId} to ${section.label}`)
                    setDragged(null)
                  }}>
                  Drop in {section.label}
                </button>
              )}
              {!section.items.length && <p className={s.empty}>Drop or add fields here.</p>}
            </div>
          </section>
          )
        })}
      </div>

      {editMode === 'definition' && <aside className={s.picker} aria-label="Unplaced fields">
        <header><strong>Field picker</strong><span>{draft.unplaced.length} unplaced</span></header>
        <label className={s.search}><Search aria-hidden="true" /><Input value={filter} placeholder="Search fields…"
          aria-label="Search unplaced fields" onChange={(event) => setFilter(event.target.value)} /></label>
        <div className={s.available}>
          {available.map((item) => (
            <div key={item.id} className={s.availableRow}>
              <EntityIcon iconKey={item.kind} size={16} />
              <span>{nameOf(item, itemLabels)}</span><span className={s.grow} />
              <CustomizeRowMenu label={nameOf(item, itemLabels)}
                sections={draft.sections.map((section) => ({
                  key: section.id, label: section.label,
                  onSelect: () => change(placeLayoutItem(draft, item.id, section.id), `Added ${nameOf(item, itemLabels)} to ${section.label}`),
                }))} />
            </div>
          ))}
          {!available.length && <p className={s.empty}>{draft.unplaced.length ? 'No matching fields.' : 'Every available item is placed.'}</p>}
        </div>
      </aside>}

      <p className={s.srOnly} role="status">{message}</p>
      <ActionBar sticky testId="layout-customize-dock" label="Customization actions"
        status={<span>{dirty ? 'Unsaved layout changes' : 'No layout changes'} · {layerLabel}</span>}
        notice={editMode === 'order'
          ? 'Your personal order stays in this environment. Field meaning and the governed form definition do not change.'
          : 'Persona and solution defaults travel through ALM. Your personal override stays in this environment.'}
        secondaryActions={<>
          {onReset && <Button variant="ghost" icon={<RotateCcw />} disabled={busy} onClick={onReset}>Reset to inherited</Button>}
          <Button variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
        </>}
        primaryAction={<Button variant="primary" disabled={!dirty || busy} onClick={save}>Save customization</Button>} />
    </section>
    </div>
  )
}

/* Re-declared beside the component whose props are written in it, so a host
   can type the data it hands this component from `@solum/ui` alone (#2406,
   design-sync kit pass: "hosts need @solum/experience to type their data").
   An alias, not a copy — the shape stays Experience's. */
export type LayoutCatalog = GovernedLayoutCatalog
export type PersistedLayoutDocument = GovernedPersistedLayoutDocument
export type LayoutDocument = GovernedLayoutDocument
